// The agent store grid: published agents, click one to start a thread.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Search } from "lucide-react";
import * as React from "react";

import { Link } from "@/components/ui/link";
import { Input, Skeleton } from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { createConversation, listStoreAgents } from "@/lib/api/chat";
import { useRouter } from "@/lib/navigation";
import { useMe } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { StoreAgent } from "@/types/api";

/** Start a conversation with an agent and open it. */
function useStartChat() {
  const router = useRouter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentSlug: string) => createConversation(agentSlug),
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: qk.chatConversations });
      router.push(`/chat/${c.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function AgentGrid({ limit }: { limit?: number }) {
  const { data: me } = useMe();
  const store = useQuery({ queryKey: qk.storeAgents, queryFn: listStoreAgents });
  const start = useStartChat();
  const [q, setQ] = React.useState("");

  if (store.isError) return <ErrorState error={store.error} onRetry={() => store.refetch()} />;
  if (store.isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }
  const agents = (store.data ?? []).filter(
    (a) =>
      !q ||
      `${a.name} ${a.description ?? ""} ${a.domain_name}`.toLowerCase().includes(q.toLowerCase()),
  );
  if ((store.data ?? []).length === 0) {
    const builder = me?.role === "tenant_admin" || me?.role === "engineer";
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No agents have been published yet.
        {builder ? (
          <>
            {" "}
            Publish a version from{" "}
            <Link href="/agents" className="text-primary underline">
              Agents
            </Link>{" "}
            to make it available here.
          </>
        ) : (
          " Ask your organisation's admin to publish one."
        )}
      </div>
    );
  }
  const shown = limit ? agents.slice(0, limit) : agents;
  return (
    <div className="space-y-4">
      {!limit ? (
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search agents"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search agents"
          />
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {shown.map((a) => (
          <AgentCard
            key={a.slug}
            agent={a}
            pending={start.isPending}
            onStart={() => start.mutate(a.slug)}
          />
        ))}
      </div>
      {limit && agents.length > limit ? (
        <Link href="/chat/agents" className="block text-center text-sm text-primary">
          See all {agents.length} agents
        </Link>
      ) : null}
    </div>
  );
}

function AgentCard({
  agent,
  pending,
  onStart,
}: {
  agent: StoreAgent;
  pending: boolean;
  onStart: () => void;
}) {
  return (
    <button
      type="button"
      disabled={pending}
      onClick={onStart}
      className="flex items-start gap-3 rounded-md border border-border bg-card p-4 text-left transition-colors hover:border-primary/60 hover:bg-muted/40 disabled:opacity-60"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        <Bot className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block font-medium">{agent.name}</span>
        <span className="line-clamp-2 block text-sm text-muted-foreground">
          {agent.description || agent.domain_name}
        </span>
      </span>
    </button>
  );
}
